/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { Suspense, use, useEffect, useMemo, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperBasketsV2 } from '@/scapi';
import { Checkbox } from '@/components/forms/checkbox';
import { Typography } from '@/components/typography';
import { useItemFetcher } from '@/hooks/use-item-fetcher';
import { useBasketUpdater } from '@/providers/basket';
import { useToast } from '@/components/toast';
import { useExtendCartOffersPromise } from '@/providers/extend-cart-offers';
import type { ExtendOffer } from '@/lib/api/extend.server';
import { getPlanFamilyLabelKey, resolveExtendPlanFamily } from '@/lib/extend-plan-family';
import type { EnrichedProductItem } from '@/lib/product/product-utils';
import { cn } from '@/lib/utils';

interface CartExtendToggleProps {
    product: EnrichedProductItem;
    className?: string;
    /**
     * Test / Storybook escape hatch. When provided, bypasses the
     * `<ExtendCartOffersProvider>` lookup and the `use()` Suspense path,
     * rendering directly from the supplied offer. `null` is a valid value
     * meaning "no live offer for this productId" → component hides.
     * Production callers never pass this; the cart route relies on the
     * context-backed promise.
     */
    offer?: ExtendOffer | null;
}

/**
 * INT-02 — Cart-row "+ Extend" protection-plan toggle.
 *
 * Eligibility source of truth:
 *   `POST /custom/ashley-extend/v1/offers/cart` — the cart route loader
 *   fires this with all merchandise PLIs on every navigation/refresh and
 *   exposes the response via `<ExtendCartOffersProvider>`. The toggle
 *   reads the per-productId offer; we render only when the live response
 *   marks the productId eligible. The cached `c_protectionPlanEligible`
 *   attribute is *not* consulted — it can drift from current eligibility
 *   (price changes, Extend portal config changes, etc.) and the project
 *   contract calls for the POST response to drive the decision.
 *
 * Selected-state source of truth:
 *   The cart PLI's `c_protectionPlanSelected` custom attribute (mirrored
 *   from the SFCC basket by `ExtendBasketEnricher`). Toggling submits a
 *   PATCH via `/action/cart-item-extend-toggle`, which trips the
 *   `basket.item.afterPATCH` hook → reconciliation upserts / removes the
 *   matching warranty PLI.
 *
 * Visual recipe (Figma node 13398:131218 cart Add-On variant):
 *   `bg-secondary` slim bar with a checkbox, the Extend® brand wordmark
 *   image, the fixed "Furniture Protection" plan-type label, and an
 *   optional "LEARN MORE" eyebrow link that opens the hosted Extend
 *   plan-details page in a new tab. Per merchandiser direction the label
 *   is fixed copy — it does not vary with the offer's `planType`.
 *
 * Brand wordmark source — same contract the PDP modal uses:
 *   `liveOffer.marketing.learnMoreModal.logoUrl`, sourced upstream from
 *   `marketing.<planType>.productDetailsPageV2.headlineCTALogoImageUrl`
 *   on the Extend Offers response and forwarded by
 *   `int_sfnext_extend/.../ashley-extend` Custom API. When the URL is
 *   absent (partially-configured Extend portal) we fall back to a styled
 *   text wordmark so the row never renders an empty slot.
 *
 * Plan-details URL — replaces the legacy SFRA order-summary "Indoor
 * Furniture" anchor (`<a class="extend-protection-summary-link" …>`)
 * per the Jun 2026 merchandiser ask. Sourced from
 * `liveOffer.marketing.learnMoreModal.planDetailsUrl` (== the same
 * upstream `plan.url` field the cartridge stamps onto warranty PLIs as
 * `c_planDetailsUrl`). When the URL is absent we hide the LEARN MORE
 * link entirely — the row falls back to the checkbox + wordmark + plan
 * type label, matching the Figma "minimal" variant.
 *
 * Optimistic UI: the checkbox reflects `fetcher.formData?.selected` while
 * in flight so the shopper sees the new state immediately. On error we
 * revert and surface a toast.
 *
 * Rendering pipeline:
 *   <CartExtendToggle>          ← exported wrapper, hides on warranty PLIs
 *     <Suspense fallback={null}>
 *       <CartExtendToggleInner> ← suspends on the offers promise (`use()`)
 *
 * Falling back to `null` while the promise resolves keeps the remove button
 * (rendered as a sibling in the cart row) painting immediately and avoids
 * a skeleton on a small below-the-fold control.
 */

function readSelected(product: EnrichedProductItem): boolean {
    return product.c_protectionPlanSelected === true;
}

/**
 * SSF-444 — Derive an "offer-like" object from the merchandise PLI's
 * persisted Extend attributes. Used as a fallback when the live
 * `/offers/cart` POST is unavailable (empty response, transport failure,
 * offers-provider not mounted) but the reconciler has ALREADY marked
 * this line item as `c_protectionPlanEligible = true` — meaning a
 * warranty PLI is on the basket and the order summary is charging for
 * it. Without this fallback the toggle row flickers off intermittently
 * on cart mutations even though the shopper's protection plan is
 * clearly attached; SSF-444 was raised when the "Indoor Furniture -
 * $159.99" chip vanished from cart line items until page refresh.
 *
 * Priority: `liveOffer` when present ALWAYS wins — persisted attrs are
 * only ever a fallback. The synthesized offer intentionally omits
 * `marketing.learnMoreModal.logoUrl` and `.planDetailsUrl` (persisted
 * attrs don't carry them) so the component naturally degrades to the
 * text wordmark and hides LEARN MORE — but the checkbox, plan family,
 * and label remain visible, which is what the shopper needs to see
 * that their coverage is still on the cart.
 *
 * @param product enriched merchandise PLI (from `ExtendBasketEnricher`)
 * @returns a partial-but-eligible offer, or `null` when the PLI is not
 *   flagged as protection-eligible.
 */
function deriveFallbackOfferFromPli(product: EnrichedProductItem): ExtendOffer | null {
    if (product.c_protectionPlanEligible !== true) return null;
    const planSku = typeof product.c_protectionPlanType === 'string' ? product.c_protectionPlanType : null;
    if (!planSku) return null;
    const termMonths = typeof product.c_extendOfferTermMonths === 'number' ? product.c_extendOfferTermMonths : null;
    // The persisted PLI carries the SFCC warranty SKU but not the
    // upstream `planType` (adh / base) discriminator. Deriving the plan
    // family from the SKU keeps the label stable across the transient
    // gap where the live offer is missing:
    //   - `FURMATT*` → mattress
    //   - anything else → furniture (accident always requires the live
    //     `planType === 'adh'` signal; we don't try to reconstruct it
    //     from the SKU because ADH plans share SKU prefixes with base).
    // The reconciler will overwrite this the moment the next live
    // offers response arrives, so the persistent state is only ever
    // used to bridge the gap.
    const derivedFamily: 'furniture' | 'mattress' = planSku.startsWith('FURMATT') ? 'mattress' : 'furniture';
    return {
        productId: product.productId ?? '',
        eligible: true,
        ashleySku: planSku,
        planPriceCents: null,
        termMonths,
        planType: null,
        planFamily: derivedFamily,
        marketingText: null,
        marketing: null,
    };
}

interface CartExtendToggleInnerProps {
    product: EnrichedProductItem;
    className?: string;
    liveOffer: ExtendOffer | null;
}

function CartExtendToggleInner({ product, className, liveOffer }: CartExtendToggleInnerProps): ReactElement | null {
    const { t } = useTranslation('extendProtectionPlan');
    const { addToast } = useToast();
    const updateBasket = useBasketUpdater();

    const itemId = product.itemId ?? '';
    const persistedSelected = readSelected(product);

    const fetcher = useItemFetcher({ itemId, componentName: 'cart-extend-toggle' });
    const isSubmitting = fetcher.state === 'submitting';

    // Optimistic state — show the pending selection while the PATCH is in flight.
    const pendingSelected = useMemo(() => {
        if (!fetcher.formData) return null;
        const raw = fetcher.formData.get('selected');
        if (raw === 'true') return true;
        if (raw === 'false') return false;
        return null;
    }, [fetcher.formData]);

    const selected = pendingSelected ?? persistedSelected;

    // Sync basket + surface failures coming back from the server action.
    const processedDataRef = useRef<typeof fetcher.data>(null);
    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data || fetcher.data === processedDataRef.current) {
            return;
        }
        processedDataRef.current = fetcher.data;

        if (fetcher.data.success && fetcher.data.basket) {
            updateBasket(fetcher.data.basket as ShopperBasketsV2.schemas['Basket']);
            return;
        }

        addToast(t('cart.updateFailed'), 'error');
        // intentionally exclude addToast/updateBasket — both are stable refs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetcher.state, fetcher.data, t]);

    // SSF-444 — The live POST `/offers/cart` response is the authoritative
    // eligibility gate WHEN it succeeds. On transient failure (network
    // blip, provider not yet mounted, `use()` resolved with `{}`) we fall
    // back to the merchandise PLI's persisted Extend attrs so the row
    // doesn't disappear while the customer still has an attached warranty
    // PLI in the basket (which the order summary continues to charge for).
    // The persisted-state view is intentionally minimal — degraded logo
    // slot + no LEARN MORE link — but the checkbox stays visible so the
    // shopper can still opt out.
    const effectiveOffer = liveOffer && liveOffer.eligible ? liveOffer : deriveFallbackOfferFromPli(product);
    if (!itemId || !effectiveOffer || !effectiveOffer.eligible) return null;

    // SSF-346 / AC #3 — Plan-type label rendered next to / below the
    // Extend wordmark. Driven by the cartridge-supplied `planFamily`
    // discriminator so the cart row matches the PDP banner's label for
    // the same productId (accident / furniture / mattress). Falls back
    // to `planType` when older cartridge builds omit `planFamily` — the
    // PLI does not carry the product's `mattressProtectorToggle`, so
    // mattress-vs-furniture inference for the silent-fallback case
    // happens server-side on the offer the cart loader fetches. The
    // legacy "always Furniture Protection" hardcode is superseded.
    const planFamily = resolveExtendPlanFamily({
        planFamily: effectiveOffer.planFamily,
        planType: effectiveOffer.planType,
    });
    const planTypeLabel = t(getPlanFamilyLabelKey(planFamily));
    // Hosted Extend wordmark URL — pulled from the live offer's
    // `marketing.learnMoreModal.logoUrl`, sourced upstream from
    // `marketing.<planType>.productDetailsPageV2.headlineCTALogoImageUrl`
    // on the Extend Offers response. Same contract the PDP modal uses.
    // Absent on the persisted-PLI fallback path — the text wordmark
    // takes over automatically via the `logoUrl` branch below.
    const logoUrl = effectiveOffer.marketing?.learnMoreModal?.logoUrl ?? null;
    // Hosted Extend plan-details page URL (`plan.url` on the upstream
    // Offers response). Powers the "LEARN MORE" external link; same URL
    // the reconciler stamps on warranty PLIs as `c_planDetailsUrl`.
    // Absent on the persisted-PLI fallback path (the merchandise PLI
    // does not carry `c_planDetailsUrl` — only the warranty PLI does),
    // in which case we hide the LEARN MORE anchor.
    const rawPlanDetailsUrl = effectiveOffer.marketing?.learnMoreModal?.planDetailsUrl;
    const planDetailsUrl =
        typeof rawPlanDetailsUrl === 'string' && rawPlanDetailsUrl.trim() ? rawPlanDetailsUrl.trim() : null;
    const checkboxId = `cart-extend-toggle-${itemId}`;

    const handleToggle = (next: boolean | 'indeterminate') => {
        if (isSubmitting) return;
        const nextSelected = next === true;
        if (nextSelected === selected) return;

        const formData = new FormData();
        formData.append('itemId', itemId);
        formData.append('selected', nextSelected ? 'true' : 'false');
        void fetcher.submit(formData, {
            method: 'PATCH',
            action: '/action/cart-item-extend-toggle',
        });
    };

    return (
        <div
            className={cn('flex items-start gap-2 bg-secondary p-3', className)}
            data-testid={`cart-extend-toggle-${itemId}`}>
            {/*
             * Checkbox owns its own click target. We intentionally do NOT
             * wrap the logo / planType label in a `<label htmlFor={checkboxId}>`
             * — doing so would make a click anywhere in the row toggle the
             * checkbox, which the merchandiser asked us to avoid (only the
             * checkbox box itself should toggle). The Checkbox carries its
             * own `aria-label` so screen readers still have a name.
             */}
            <Checkbox
                id={checkboxId}
                checked={selected}
                onCheckedChange={handleToggle}
                disabled={isSubmitting}
                aria-label={t('cart.checkboxAriaLabel')}
                aria-busy={isSubmitting}
                className="mt-0.5 size-4"
            />
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                {logoUrl ? (
                    // `max-w-[80px]` caps the wordmark at the Figma-spec'd
                    // ~64px slot regardless of the SVG's intrinsic width
                    // (the Extend portal serves a single asset reused
                    // across surfaces and its viewBox can be wide).
                    <img
                        src={logoUrl}
                        alt={t('cart.extendLogoAlt')}
                        loading="lazy"
                        decoding="async"
                        className="mt-0.5 block h-[14px] w-auto max-w-[80px] object-contain"
                    />
                ) : (
                    // Fallback styled wordmark — keeps the slot weighted
                    // even when the Extend portal hasn't supplied a logo
                    // URL (mirrors the PDP modal's BrandLockup fallback).
                    <Typography asChild variant="small" className="mt-0.5 font-semibold leading-5">
                        <span>
                            {t('cart.title')}
                            <sup className="text-[0.6em]">®</sup>
                        </span>
                    </Typography>
                )}
                <span className="text-xs md:text-sm font-semibold leading-5">{planTypeLabel}</span>
                {planDetailsUrl && (
                    // External anchor — opens the hosted Extend plan-details
                    // page in a new tab. Replaces the legacy SFRA
                    // `<a class="extend-protection-summary-link" target="_blank">`
                    // that used to live in the order-summary plan label.
                    // `noopener noreferrer` shields against reverse-tabnabbing
                    // (Extend hosts the destination, so we treat it as
                    // third-party).
                    <a
                        href={planDetailsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="extend-protection-summary-link text-xs font-semibold uppercase tracking-[1.8px] hover:underline"
                        data-testid={`cart-extend-toggle-learn-more-${itemId}`}>
                        {t('cart.learnMore')}
                    </a>
                )}
            </div>
        </div>
    );
}

/**
 * Resolves the per-row live offer from the cart-route offers promise (via
 * `use()`) and forwards it to `CartExtendToggleInner`. Mounted only when no
 * `offer` prop is passed (production / non-test callers).
 */
function CartExtendToggleFromContext({
    product,
    className,
}: Omit<CartExtendToggleProps, 'offer'> & { product: EnrichedProductItem }): ReactElement | null {
    const offersPromise = useExtendCartOffersPromise();
    if (!offersPromise) return null;
    // Suspends on first render; the cart-route loader resolves with `{}`
    // even on transport failure, so this `use()` never throws on rejection.
    const offersByProductId = use(offersPromise);
    const productId = product.productId ?? '';
    const liveOffer = productId ? (offersByProductId[productId] ?? null) : null;
    return <CartExtendToggleInner product={product} className={className} liveOffer={liveOffer} />;
}

export function CartExtendToggle({ product, className, offer }: CartExtendToggleProps): ReactElement | null {
    // Pre-Suspense guards — cheap checks that avoid mounting a Suspense
    // boundary for items that will never qualify.
    if (!product.itemId) return null;
    if (product.c_isProtectionPlan === true) return null;

    // Test / Storybook path — render directly from the supplied offer,
    // skipping Suspense entirely. `null` means "no live offer" → hide.
    if (offer !== undefined) {
        return <CartExtendToggleInner product={product} className={className} liveOffer={offer} />;
    }

    return (
        <Suspense fallback={null}>
            <CartExtendToggleFromContext product={product} className={className} />
        </Suspense>
    );
}

export default CartExtendToggle;
